import { sanitizeCharacterSlug } from "../protagonist.js";
import { placeCharacter } from "./place-character.js";
/**
 * Normalize a character id to a case-insensitive map key. LS authors
 * mix `&malia look smile` (lowercase) and `@MALIA hide` (uppercase) —
 * sometimes the same script, sometimes the same character. The
 * CharacterChip in ls-studio also forces uppercase on selection, so
 * a chip-driven hide can target a key that the original script wrote
 * in lowercase. Folding to lowercase here so set/get/delete agree,
 * regardless of source casing. `working.id` preserves the original
 * authored case for display.
 */
function keyOf(id) {
    return id.toLowerCase();
}
/**
 * Apply a character.* card (show / bubble) to the working character map and
 * state. Returns the next insertion counter. Kept separate from compute.ts so
 * the main applyCard switch stays under the 300-line cap.
 *
 * Placement mirrors the ls-preview player's `forcedSlotFor` (reduce.ts):
 *   - protagonist → LEFT, everyone else → RIGHT;
 *   - at most TWO portraits — the protagonist (left) plus the most-recent
 *     other character (right). A `character.show` of a non-protagonist
 *     replaces whoever held the right slot; a `character.show` of the
 *     protagonist replaces the left.
 *   - when the protagonist is unknown (null) we fall back to the prior
 *     single-on-screen behavior: a `character.show` clears everyone and shows
 *     the one character centered, so nothing regresses for projects without a
 *     `- Protagonist:` line.
 */
export function applyCharacterCard(card, state, characters, assets, insertion, protagonist) {
    switch (card.kind) {
        case "character.show": {
            const id = stringField(card, "character");
            state.bubble = undefined;
            if (!protagonist) {
                // No MC resolved → keep the legacy one-on-screen, centered behavior.
                characters.clear();
                characters.set(keyOf(id), {
                    id,
                    insertion,
                    lane: "center",
                    look: stringField(card, "look"),
                    name: characterName(assets, id)
                });
                return insertion + 1;
            }
            const lane = placeCharacter(sanitizeCharacterSlug(id) ?? "", protagonist, "right");
            // Drop any character that already holds this lane EXCEPT this same id —
            // re-showing the on-screen character (new look) keeps the slot, and the
            // protagonist + a companion can coexist (different lanes). This bounds
            // the stage to two portraits: one left, one right.
            for (const [key, working] of characters) {
                if (working.lane === lane && key !== keyOf(id)) {
                    characters.delete(key);
                }
            }
            characters.set(keyOf(id), {
                id,
                insertion,
                lane,
                look: stringField(card, "look"),
                name: characterName(assets, id)
            });
            return insertion + 1;
        }
        case "character.bubble": {
            state.bubble = {
                character: characterName(assets, stringField(card, "character")),
                kind: stringField(card, "bubble")
            };
            return insertion;
        }
        default:
            return insertion;
    }
}
export function characterName(assets, id) {
    const entry = assets?.characters.find((c) => same(c.id, id) || same(c.name, id));
    return entry && !same(entry.id, entry.name) ? entry.name : id.toUpperCase();
}
function stringField(card, key) {
    const value = card.fields[key];
    return typeof value === "string" ? value : "";
}
function same(left, right) {
    return String(left ?? "").toLowerCase() === String(right ?? "").toLowerCase();
}
